import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NativeService } from './native.service';
import { environment } from '@environments/environment';
import { TokenService } from './token.service';
import { Tokens } from '../models/tokens.model';
import {
  ChangePasswordParams,
  LoginAppleParams,
  LoginEmailParams,
  LoginFacebookParams,
  LoginGooglePlayParams,
  LoginGuestParams,
  RegisterEmailParams,
  ResetPasswordParams,
  SendResetPasswordParams,
  ValidateEmailParams,
} from '../models/login-params.model';
import { LoggerService } from '@shared/services/logger.service';
import { FacebookService } from '@shared/services/facebook.service';
import { OneSignalService } from '@shared/services/one-signal.service';
import { LandingService } from '@app/landing/landing.service';
import { AppService } from '@app/app.service';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from './analytics.service';
import { lastValueFrom } from 'rxjs';
import { GlobalDataRepository } from '../state/global-data/global-data.repository';
import { getRegistry } from '@ngneat/elf';
import { UserService } from './user.service';
import { slotcomStorageStrategy } from '../state/slotcom.storage';
import { PlayerInfoAnalyticsService } from '@app/core/analytics/firehose/public-api';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private http = inject(HttpClient);
  private nativeService = inject(NativeService);
  private tokenService = inject(TokenService);
  private globalDataRepo = inject(GlobalDataRepository);
  private analyticsService = inject(AnalyticsService);
  private oneSignalService = inject(OneSignalService);
  private facebookService = inject(FacebookService);
  private appService = inject(AppService);
  private landingService = inject(LandingService);
  private logger = inject(LoggerService);
  private toastrService = inject(ToastrService);
  private userService = inject(UserService);
  private playerInfoAnalytics = inject(PlayerInfoAnalyticsService);

  private readonly apiLoginUrl = `${environment.apiUrl}/api/login`;
  private readonly apiRegisterByEmailUrl = `${environment.apiUrl}/api/registerbyemail`;
  private readonly apiLogoutUrl = `${environment.apiUrl}/api/logout`;

  async facebookLogin(loginFacebookParams: LoginFacebookParams): Promise<Tokens> {
    const tokens = await lastValueFrom(this.http.post<Tokens>(`${this.apiLoginUrl}/facebook`, loginFacebookParams));
    this.tokenService.storeTokens(tokens);
    await this.userService.fetchCurrentUser();
    this.playerInfoAnalytics.trackPlayerInfoOnLogin();
    return tokens;
  }

  async emailLogin(loginEmailParams: LoginEmailParams): Promise<Tokens> {
    const tokens = await lastValueFrom(this.http.post<Tokens>(`${this.apiLoginUrl}/email`, loginEmailParams));
    this.tokenService.storeTokens(tokens);
    await this.userService.fetchCurrentUser();
    this.playerInfoAnalytics.trackPlayerInfoOnLogin();
    return tokens;
  }

  async guestLogin(loginGuestParams: LoginGuestParams): Promise<Tokens> {
    const tokens = await lastValueFrom(this.http.post<Tokens>(`${this.apiLoginUrl}/guest`, loginGuestParams));
    this.tokenService.storeTokens(tokens);
    await this.userService.fetchCurrentUser();
    this.playerInfoAnalytics.trackPlayerInfoOnLogin();
    return tokens;
  }

  async appleLogin(loginAppleParams: LoginAppleParams): Promise<Tokens> {
    const tokens = await lastValueFrom(this.http.post<Tokens>(`${this.apiLoginUrl}/apple`, loginAppleParams));
    this.tokenService.storeTokens(tokens);
    await this.userService.fetchCurrentUser();
    this.playerInfoAnalytics.trackPlayerInfoOnLogin();
    return tokens;
  }

  async googlePlayLogin(loginParams: LoginGooglePlayParams): Promise<Tokens> {
    const tokens = await lastValueFrom(this.http.post<Tokens>(`${this.apiLoginUrl}/googlePlay`, loginParams));
    this.tokenService.storeTokens(tokens);
    await this.userService.fetchCurrentUser();
    this.playerInfoAnalytics.trackPlayerInfoOnLogin();
    return tokens;
  }

  async sendResetPassword(sendResetPasswordParams: SendResetPasswordParams) {
    return await lastValueFrom(this.http.post(`${this.apiLoginUrl}/sendResetPassword`, sendResetPasswordParams));
  }

  async resetPassword(resetPasswordParams: ResetPasswordParams) {
    const tokens = await lastValueFrom(
      this.http.post<Tokens>(`${this.apiLoginUrl}/resetPassword`, resetPasswordParams)
    );
    this.tokenService.storeTokens(tokens);
    return tokens;
  }

  async register(registerEmailParams: RegisterEmailParams) {
    const tokens = await lastValueFrom(
      this.http.post<Tokens>(`${this.apiRegisterByEmailUrl}/register`, registerEmailParams)
    );
    this.tokenService.storeTokens(tokens);
    return tokens;
  }

  async resendValidateEmail() {
    return await lastValueFrom(this.http.post(`${this.apiRegisterByEmailUrl}/resendValidateEmail`, {}));
  }

  async validateEmail(validateEmailParams: ValidateEmailParams) {
    return await lastValueFrom(this.http.post(`${this.apiRegisterByEmailUrl}/validateEmail`, validateEmailParams));
  }

  async changePassword(changePasswordParams: ChangePasswordParams) {
    return await lastValueFrom(this.http.post(`${this.apiLoginUrl}/changepassword`, changePasswordParams));
  }

  async logout() {
    this.toastrService.clear();
    await this.landingService.showLogout();

    try {
      await Promise.allSettled([this.setLogoutActions()]);
    } catch (e) {
      this.logger.error('Unexpected error during the logout of the dependencies', e);
    }

    this.tokenService.removeTokens();
    this.resetStores(['version', 'global-data']);
    this.deleteStores(['tester']);
    this.appService.reload();
  }

  async resetElfStores() {
    await this.landingService.showLogout();

    this.resetStores(['user']);

    window.location.reload();
  }

  private resetStores(excluded?: string[]) {
    getRegistry().forEach((store) => {
      if (!excluded || !excluded.includes(store.name)) {
        store.reset();
      }
    });
  }

  private deleteStores(stores?: string[]) {
    if (!stores) return;

    getRegistry().forEach((store) => {
      if (stores.includes(store.name)) {
        store.destroy();
        slotcomStorageStrategy.removeItem(store.name);
      }
    });
  }

  private setLogoutActions() {
    const logoutActions: Promise<void>[] = [];
    logoutActions.push(lastValueFrom(this.http.post<void>(this.apiLogoutUrl, {})));
    logoutActions.push(this.analyticsService.logEvent('ps_logout'));
    logoutActions.push(this.oneSignalService.logout());
    logoutActions.push(this.nativeService.logout());

    if (this.globalDataRepo.getLoginMethod() === 'facebook') {
      logoutActions.push(this.facebookService.logout());
    }

    return logoutActions;
  }
}
